import React, { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useToast } from "../components/Toast";
import { apiClient } from "../services/api";
import ModelSelector from "../components/ModelSelector";

interface CreateImageProps {
  mode?: "t2i" | "edit";
}

const CreateImage: React.FC<CreateImageProps> = ({ mode: propMode }) => {
  const location = useLocation();
  const { showToast } = useToast();

  // Showcase mode - no user data
  const isFreePlan = true;

  // Form state
  const [prompt, setPrompt] = useState("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [modelCredits, setModelCredits] = useState<Record<string, number>>({});
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const isSignedIn = false; // Showcase mode
  const batchQuantity = 1; // Fixed for showcase


  // Get the appropriate model type based on mode
  const getModelType = useCallback((): "t2i" | "i2i" => {
    return propMode === "edit" ? "i2i" : "t2i";
  }, [propMode]);

  // Load model credits on mount
  useEffect(() => {
    const loadModelCredits = async () => {
      try {
        const modelType = getModelType();
        const response = await apiClient.getModels({ type: modelType });
        const credits: Record<string, number> = {};
        response.models.forEach((model) => {
          credits[model.name] = model.credits;
        });
        setModelCredits(credits);
      } catch (error) {
        console.error("Failed to load model credits:", error);
        // Set default credits as fallback
        setModelCredits({
          "flux-dev": 15,
          "flux-pro": 25,
          "flux-1.1-pro": 30,
        });
      }
    };

    loadModelCredits();
  }, [propMode, getModelType]);

  // Track page view on mount (disabled for showcase)
  useEffect(() => {
    // Analytics removed for showcase site
  }, [propMode]);

  // Reset selected model when mode changes
  useEffect(() => {
    setSelectedModel(""); // Reset to empty, ModelSelector will pick the first model
  }, [propMode]);

  // Save user input data to localStorage
  useEffect(() => {
    // Only save to localStorage when user has actual content and is not signed in
    if (!isSignedIn && (prompt.trim() || sourceImage)) {
      const createImageData = {
        prompt,
        sourceImage,
        selectedModel,
        mode: propMode,
        timestamp: Date.now(),
      };
      localStorage.setItem("createimage_data", JSON.stringify(createImageData));
      console.log(
        "💾 Saved CreateImage data to localStorage:",
        createImageData
      );
    }
  }, [prompt, sourceImage, selectedModel, propMode, isSignedIn]);

  // Restore user input after authentication
  useEffect(() => {
    if (isSignedIn) {
      const savedData = localStorage.getItem("createimage_data");
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          console.log("📦 Found saved CreateImage data:", parsed);

          // Only restore if data is recent (within 30 minutes) and mode matches
          if (
            Date.now() - parsed.timestamp < 30 * 60 * 1000 &&
            parsed.mode === propMode
          ) {
            let dataRestored = false;

            if (parsed.prompt) {
              setPrompt(parsed.prompt);
              console.log(
                "🔄 Restored prompt from localStorage:",
                parsed.prompt
              );
              dataRestored = true;
            }
            if (parsed.sourceImage) {
              setSourceImage(parsed.sourceImage);
              console.log("🔄 Restored source image from localStorage");
              dataRestored = true;
            }
            if (parsed.selectedModel) {
              setSelectedModel(parsed.selectedModel);
              console.log(
                "🔄 Restored selected model from localStorage:",
                parsed.selectedModel
              );
              dataRestored = true;
            }

            // Clear localStorage after successful restoration
            if (dataRestored) {
              localStorage.removeItem("createimage_data");
              console.log(
                "🗑️ Cleared CreateImage data from localStorage after restoration"
              );
            }
          } else {
            if (Date.now() - parsed.timestamp >= 30 * 60 * 1000) {
              console.log("⏰ Saved data is too old, not restoring");
            } else {
              console.log("🔄 Mode mismatch, not restoring data");
            }
            // Also clear old or mismatched data
            localStorage.removeItem("createimage_data");
          }
        } catch (error) {
          console.error("Failed to restore createimage data:", error);
        }
      } else {
        console.log("📭 No saved CreateImage data found");
      }
    }
  }, [isSignedIn, propMode]);

  // UI state
  const [isStartingGeneration, setIsStartingGeneration] = useState(false);

  // Check if referral card should be shown
  // useEffect(() => {
  //   if (
  //     isSignedIn &&
  //     userData &&
  //     selectedModel &&
  //     modelCredits[selectedModel]
  //   ) {
  //     const creditsRequired = modelCredits[selectedModel];
  //     const availableCredits =
  //       userData.limits.max_credits - userData.limits.credits;
  const checkForClerkPopoverAndProceed = useCallback(async () => {
    if (!prompt.trim()) {
      showToast({ type: "error", title: "Please enter a prompt" });
      return;
    }

    // For image editing mode, require a source image
    if (propMode === "edit" && !sourceImage) {
      showToast({ type: "error", title: "Please select an image to edit" });
      return;
    }

    setIsStartingGeneration(true);

    try {
      // Build redirect URL with all form data
      const currentPath = location.pathname;
      const params = new URLSearchParams({
        utm_source: 'pixelmaker_io',
        prompt: prompt,
        model: selectedModel,
      });

      // Upload source image if exists and get S3 URL
      if (sourceImage && propMode === "edit") {
        const uploadResponse = await fetch('https://api.pixelmaker.ai/temp-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: sourceImage })
        });
        const uploadResult = await uploadResponse.json();
        if (uploadResult.url) {
          params.append('source_image', uploadResult.url);
        }
      }

      window.location.href = `https://pixelmaker.ai${currentPath}?${params.toString()}`;
    } catch (error) {
      console.error('Failed to upload image:', error);
      showToast({ type: "error", title: "Failed to upload image. Please try again." });
      setIsStartingGeneration(false);
    }
  }, [
    prompt,
    showToast,
    selectedModel,
    propMode,
    sourceImage,
    location.pathname,
  ]);

  // Showcase mode - removed unused handlers

  // Render different workflows based on mode
  const renderWorkflow = () => {
    if (propMode === "t2i" || !propMode) {
      return (
        <div className="space-y-6">
          {/* Page Header */}
          <div className="text-left mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Text to Image
            </h1>
            <p className="text-gray-600 dark:text-white text-lg">
              Create stunning images from text descriptions using advanced AI
              models.
            </p>
          </div>

          {/* Form Content */}
          <div className="space-y-6">
            {/* Model Selection */}
            <div>
              <ModelSelector
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                onModelCreditsChange={setModelCredits}
                type={getModelType()}
              />
            </div>

            {/* Prompt Input */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Your Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe your image. For example: A T-Rex relaxing on a beach, lying on a sun lounger and wearing sunglasses."
                className="w-full h-40 px-6 py-4 bg-white/5 dark:bg-white/5 border border-white/20 dark:border-white/20 rounded-xl focus:outline-none focus:ring-0 focus:border-white/40 dark:focus:border-white/40 resize-none text-base leading-relaxed text-white dark:text-white placeholder-gray-400 dark:placeholder-gray-400 transition-all duration-200"
              />

              {/* Character count */}
              <div className="absolute bottom-3 right-3">
                <span className="text-xs text-gray-400 dark:text-gray-400 bg-white/10 dark:bg-white/10 px-2 py-1 rounded-md">
                  {prompt.length}/500
                </span>
              </div>
            </div>

            <div className="space-y-3 flex flex-col items-center">
              <button
                onClick={checkForClerkPopoverAndProceed}
                disabled={!prompt.trim() || isStartingGeneration}
                className="w-3/4 md:w-1/2 bg-white text-black hover:bg-gray-200 disabled:bg-gray-700 disabled:cursor-not-allowed px-6 py-3 rounded-full font-medium transition-all duration-200"
              >
                {isStartingGeneration ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </div>
                ) : (
                  "Generate Image"
                )}
              </button>

              {/* Generation Info */}
              <div className="text-center text-sm text-gray-600 dark:text-white">
                <div className="flex items-center justify-center gap-4">
                  <span>Est. {batchQuantity > 1 ? `${30 * batchQuantity} seconds` : '30 seconds'}</span>
                  {!isFreePlan && (
                    <div className="flex items-center gap-1">
                      <svg
                        className="w-3 h-3 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5.5H11.5v1.15c-1.38.21-2.58 1.08-2.58 2.54 0 1.54 1.17 2.42 3.16 2.96 1.77.48 2.24 1.12 2.24 1.83 0 .78-.79 1.52-2.1 1.52-1.63 0-2.24-.84-2.32-1.64H8.19c.1 1.7 1.36 2.66 2.98 2.97V18.5h1.73v-1.19c1.42-.21 2.63-1.03 2.63-2.58 0-1.82-1.34-2.66-3.22-3.09z" />
                      </svg>
                      <span>
                        {batchQuantity > 1 
                          ? `${(modelCredits[selectedModel] || 15) * batchQuantity} credits (${batchQuantity} × ${modelCredits[selectedModel] || 15})`
                          : `${modelCredits[selectedModel] || 15} credits`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (propMode === "edit") {
      return (
        <div className="space-y-6">
          {/* Page Header */}
          <div className="text-left mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Image Editing
            </h1>
            <p className="text-gray-600 dark:text-white text-lg">
              Upload or select an image and describe how you want to edit it
              using AI.
            </p>
          </div>

          {/* Form Content */}
          <div className="space-y-6">
            {/* Model Selection */}
            <div>
              <ModelSelector
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                onModelCreditsChange={setModelCredits}
                type={getModelType()}
              />
            </div>

            {/* Image Upload/Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Source Image
              </label>
                <button
                  onClick={() =>
                    document.getElementById("image-upload")?.click()
                  }
                className="w-full border-2 border-dashed border-gray-300 dark:border-white rounded-lg p-6 text-center hover:border-blue-400 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <svg
                    className="w-8 h-8 text-gray-400 dark:text-white mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm text-gray-600 dark:text-white">
                    Upload Image
                  </p>
                </button>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) =>
                      setSourceImage(e.target?.result as string);
                    reader.readAsDataURL(file);
                  }
                }}
                className="hidden"
              />
            </div>

            {/* Show selected image */}
            {sourceImage && (
              <div className="flex justify-center">
                <div className="border border-white/20 rounded-lg p-4 inline-block max-w-full">
                <div className="relative inline-block">
                  <img
                    src={sourceImage}
                    alt="Source"
                    className="h-[400px] w-auto object-cover rounded-lg"
                  />
                  <button
                    onClick={() => setSourceImage(null)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                    aria-label="Remove image"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                </div>
              </div>
            )}

            {/* Editing Prompt */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                Editing Instructions
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe how you want to edit the image. For example: Change the background to a sunset beach scene, or Add sunglasses to the person."
                className="w-full h-40 px-6 py-4 bg-white/5 dark:bg-white/5 border border-white/20 dark:border-white/20 rounded-xl focus:outline-none focus:ring-0 focus:border-white/40 dark:focus:border-white/40 resize-none text-base leading-relaxed text-white dark:text-white placeholder-gray-400 dark:placeholder-gray-400 transition-all duration-200"
              />

              {/* Character count */}
              <div className="absolute bottom-3 right-3">
                <span className="text-xs text-gray-400 dark:text-gray-400 bg-white/10 dark:bg-white/10 px-2 py-1 rounded-md">
                  {prompt.length}/500
                </span>
              </div>
            </div>

            <div className="space-y-3 flex flex-col items-center">
              <button
                onClick={checkForClerkPopoverAndProceed}
                disabled={
                  !sourceImage || !prompt.trim() || isStartingGeneration
                }
                className="w-3/4 md:w-1/2 bg-white text-black hover:bg-gray-200 disabled:bg-gray-700 disabled:cursor-not-allowed px-6 py-3 rounded-full font-medium transition-all duration-200"
              >
                {isStartingGeneration ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Editing...
                  </div>
                ) : (
                  "Edit Image"
                )}
              </button>

              {/* Generation Info */}
              <div className="text-center text-sm text-gray-600 dark:text-white">
                <div className="flex items-center justify-center gap-4">
                  <span>Est. {batchQuantity > 1 ? `${30 * batchQuantity} seconds` : '30 seconds'}</span>
                  {!isFreePlan && (
                    <div className="flex items-center gap-1">
                      <svg
                        className="w-3 h-3 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5.5H11.5v1.15c-1.38.21-2.58 1.08-2.58 2.54 0 1.54 1.17 2.42 3.16 2.96 1.77.48 2.24 1.12 2.24 1.83 0 .78-.79 1.52-2.1 1.52-1.63 0-2.24-.84-2.32-1.64H8.19c.1 1.7 1.36 2.66 2.98 2.97V18.5h1.73v-1.19c1.42-.21 2.63-1.03 2.63-2.58 0-1.82-1.34-2.66-3.22-3.09z" />
                      </svg>
                      <span>
                        {batchQuantity > 1 
                          ? `${(modelCredits[selectedModel] || 15) * batchQuantity} credits (${batchQuantity} × ${modelCredits[selectedModel] || 15})`
                          : `${modelCredits[selectedModel] || 15} credits`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Fallback
    return (
      <div className="bg-white/5 dark:bg-white/5 rounded-xl shadow-sm border border-white/20 dark:border-white/20 p-8 transition-colors duration-300">
        <p className="text-center text-gray-500 dark:text-white">
          Please select a creation mode from the tabs above.
        </p>
      </div>
    );
  };

  // Show form (default view)
  return (
    <div className="bg-black dark:bg-black p-6 pb-48 md:pb-24 transition-colors duration-300">
      <div className="max-w-6xl mx-auto">
        {/* Main Content */}
        {renderWorkflow()}
      </div>
    </div>
  );
};

export default CreateImage;
